'use strict';

import _ from 'lodash';
import moment from 'moment';

import ModuleProvider from 'ck-core/source/system/ModuleProvider';
import { UserBadge } from 'ck-users/source/functions/userProgress';

const rarityStringMapping: {
	[index: string]: string;
} = {
	'1': 'Common',
	'2': 'Uncommon',
	'3': 'Rare',
	'4': 'Epic'
};

const rarityXpMapping: {
	[index: string]: number;
} = {
	Common: 50,
	Uncommon: 100,
	Rare: 200
};

export default class BadgeService extends ModuleProvider {
	badges: UserBadge[];
	constructor(parent: ModuleProvider, badges: UserBadge[]) {
		super(parent);
		this.badges = badges;
	}

	static getBadgeData(badgeId: string) {
		// Badges are of the form e.g. parameters-2
		// e.g. "parameters-2" -> "parameters"
		const name = badgeId.substring(0, badgeId.length - 2);
		const icon = 'badge-' + name;
		// e.g. "parameters-2" -> 2
		const rarityIndex = +badgeId.charAt(badgeId.length - 1);
		// e.g. 2 -> "rare"
		const rarity = rarityStringMapping[rarityIndex];
		return {
			badgeId,
			name,
			icon,
			rarityIndex,
			rarity
		};
	}

	static getCourseBadges(forRoblox: boolean) {
		const activities = require('ck-core/source/collections/activities')();
		const brands = require('ck-core/source/data/brands');
		const courseData = activities
			.$find({
				isCourse: true,
				active: true
			})
			.$();
		const currentBrandCourses = _.filter(
			courseData,
			course => _.includes(course.brands, brands.ROBLOX) === forRoblox
		);
		const videos = _.flatMap(currentBrandCourses, course => course.videos);
		const chapters = _.flatMap(videos, video => video.chapters);
		const badges = _.flatMap(chapters, chapter => chapter.badges);
		return _.uniq(badges);
	}
	generateBadgeTableData(forRoblox: boolean) {
		const badgeMappings = require('ck-core/source/data/BadgeMappings');
		const progressionKeys = require('ck-core/source/data/ProgressionKeys');
		const badges = BadgeService.getCourseBadges(forRoblox);
		return _.compact(
			_.map(badges, badgeId => {
				const mapItem = _.find(badgeMappings, item => item.badgeId === badgeId);
				if (mapItem) {
					const itemProgressionKeys = _.mapValues(_.keyBy(mapItem.progressionKeys), key =>
						progressionKeys.get(key)
					);
					const badgeData = BadgeService.getBadgeData(badgeId);
					return _.merge(
						{
							category: mapItem.category,
							badgeId,
							progressionKeys: itemProgressionKeys,
							rarity: badgeData.rarity,
							icon: badgeData.icon
						},
						this.getBadgeUnlockData(badgeId)
					);
				}
			})
		);
	}

	static getXpForBadge(badgeId: string): number {
		const rarityString = BadgeService.getBadgeRarityString(badgeId);

		if (rarityString in rarityXpMapping) {
			return rarityXpMapping[rarityString];
		} else {
			const message = 'Could not get XP for rarity';
			CK.logger.error(message, rarityString);
			throw new Error(`${message}: ${rarityString}`);
		}
	}

	static getBadgeRarityString(badgeId: string): string {
		const rarity = Number.parseInt(badgeId.charAt(badgeId.length - 1));
		return BadgeService.getRarityString(rarity);
	}

	static getRarityString(rarity: number): string {
		if (rarity in rarityStringMapping) {
			return rarityStringMapping[rarity];
		} else {
			const message = 'Invalid rarity';
			CK.logger.error(message, rarity);
			throw new Error(`${message}: ${rarity}`);
		}
	}

	private getBadgeUnlockData(badgeId: string) {
		const badge = _.find(this.badges, badge => badgeId === badge.id);
		return badge
			? {
					unlocked: true,
					count: badge.count,
					firstDateAwarded: badge.firstDateAwarded,
					latestDateAwarded: badge.latestDateAwarded,
					recentlyAwarded: BadgeService.badgeAwardedRecently(badge)
			  }
			: null;
	}

	private static badgeAwardedRecently(badge: any): boolean {
		const recencyInDays = 7;
		const dateLastAwarded = badge.latestDateAwarded;
		return moment().diff(dateLastAwarded, 'days') < recencyInDays;
	}
}
